import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { live } from '../data/live';
import type { ChargerModel } from '../data/live';
import { Empty, Loading, Pill } from '../components/ui';
import { errorText, loadFailedHint } from '../lib/errors';

/**
 * The charger models this network runs.
 *
 * The model on a charge point used to be a free-text box, and that was not
 * cosmetic: the server keys a model policy off the exact string. Two values
 * get the YCL handling — `YCL-dual-44KW` and the `YC-44KW` the unit reports —
 * and anything else falls back to handling that TRUSTS what a connector says
 * about a session. On that hardware trusting it is what automatically closed
 * session 15 at 0 kWh, so `YCL dual 44KW` typed with a space was one keystroke
 * from a charge a driver paid for being ended early, with nothing shown.
 *
 * So this is the list, the charge point form picks from it, and the server
 * refuses a model that is not here.
 *
 * `model` is never editable: it is what charge points already store. Retiring
 * takes a model out of what is offered while leaving the units that name it
 * readable — and, importantly, still on the right policy.
 */
export function ChargerModels() {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const { data, isLoading, error: loadError } = useQuery({
    queryKey: ['admin-charger-models'],
    queryFn: () => live.allChargerModels(),
  });

  const done = () => {
    qc.invalidateQueries({ queryKey: ['admin-charger-models'] });
    /* The charge point form offers this list, so it has to see a new model
       without a reload. */
    qc.invalidateQueries({ queryKey: ['charger-models'] });
    qc.invalidateQueries({ queryKey: ['chargepoints'] });
  };

  const add = useMutation({
    mutationFn: (v: {
      model: string; manufacturer: string; reportedModel?: string;
      powerKw?: number | null; connectors?: number | null; notes?: string;
    }) => live.addChargerModel(v),
    onSuccess: () => { done(); setAdding(false); },
    onError: (e: Error) => setFailed(errorText(e)),
  });

  const edit = useMutation({
    mutationFn: (v: {
      model: string;
      patch: {
        manufacturer?: string; reportedModel?: string; powerKw?: number | null;
        connectors?: number | null; notes?: string; active?: boolean;
      };
    }) => live.editChargerModel(v.model, v.patch),
    onSuccess: done,
    onError: (e: Error) => setFailed(errorText(e)),
  });

  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
        <div className="li-sub">
          Offered when you set the hardware on a charge point. How a charger is
          handled when it drops a session depends on which model it is set to,
          so a unit on the wrong one is handled wrongly.
        </div>
        <button className="btn sm" onClick={() => { setFailed(null); setAdding(true); }}>
          Add a model
        </button>
      </div>

      {failed && <div className="note" role="alert" style={{ marginBottom: 10, color: 'var(--bad)' }}>{failed}</div>}

      {isLoading ? (
        <Loading />
      ) : !data ? (
        <Empty title="Charger models are not available." hint={loadFailedHint(loadError)} />
      ) : !data.length ? (
        <Empty title="No charger models" hint="Add the hardware this network runs." />
      ) : (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>Model</th>
                <th>Manufacturer</th>
                <th>Reports as</th>
                <th>kW</th>
                <th>Guns</th>
                <th>In use</th>
                <th>State</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.map((m) => (
                <Row
                  key={m.model}
                  model={m}
                  pending={edit.isPending}
                  onSave={(patch) => edit.mutate({ model: m.model, patch })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <AddModel
          pending={add.isPending}
          onClose={() => setAdding(false)}
          onSubmit={(v) => add.mutate(v)}
        />
      )}
    </>
  );
}

function Row({
  model,
  pending,
  onSave,
}: {
  model: ChargerModel;
  pending: boolean;
  onSave: (patch: {
    manufacturer?: string; reportedModel?: string; powerKw?: number | null;
    connectors?: number | null; notes?: string; active?: boolean;
  }) => void;
}) {
  const [make, setMake] = useState(model.manufacturer);
  const [reported, setReported] = useState(model.reportedModel ?? '');
  const [kw, setKw] = useState(model.powerKw == null ? '' : String(model.powerKw));
  const [guns, setGuns] = useState(model.connectors == null ? '' : String(model.connectors));

  const dirty = make !== model.manufacturer
    || reported !== (model.reportedModel ?? '')
    || kw !== (model.powerKw == null ? '' : String(model.powerKw))
    || guns !== (model.connectors == null ? '' : String(model.connectors));

  return (
    <tr>
      {/* Not editable: this is the string every charge point stores and the
          one a model policy is matched against. */}
      <td className="mono">{model.model}</td>
      <td>
        <input
          className="field" value={make}
          onChange={(e) => setMake(e.target.value)}
          style={{ maxWidth: 140 }}
        />
      </td>
      <td>
        <input
          className="field mono" value={reported}
          placeholder="same"
          title="What the unit calls itself over OCPP. Capped at 20 characters by the protocol, so most abbreviate."
          onChange={(e) => setReported(e.target.value)}
          style={{ maxWidth: 120 }}
        />
      </td>
      <td>
        <input
          className="field" value={kw} inputMode="decimal"
          onChange={(e) => setKw(e.target.value.replace(/[^\d.]/g, ''))}
          style={{ maxWidth: 70 }}
        />
      </td>
      <td>
        <input
          className="field" value={guns} inputMode="numeric"
          onChange={(e) => setGuns(e.target.value.replace(/\D/g, ''))}
          style={{ maxWidth: 60 }}
        />
      </td>
      <td className="li-sub">
        {model.inUse === 0 ? '—' : `${model.inUse} charge point${model.inUse === 1 ? '' : 's'}`}
      </td>
      <td>
        <Pill tone={model.active ? 'on' : 'off'}>
          {model.active ? 'offered' : 'retired'}
        </Pill>
      </td>
      <td className="act">
        {dirty && (
          <button
            className="btn sm"
            disabled={pending || make.trim().length < 2}
            onClick={() => onSave({
              manufacturer: make.trim(),
              reportedModel: reported.trim(),
              powerKw: kw.trim() === '' ? null : Number(kw),
              connectors: guns.trim() === '' ? null : Number(guns),
            })}
          >
            Save
          </button>
        )}
        <button
          className="btn sm ghost"
          disabled={pending}
          title={model.active
            ? 'Stops it being offered on new charge points. Units already set to it keep it, and keep being handled by it.'
            : 'Offers it again.'}
          onClick={() => onSave({ active: !model.active })}
        >
          {model.active ? 'Retire' : 'Offer again'}
        </button>
      </td>
    </tr>
  );
}

function AddModel({
  pending,
  onClose,
  onSubmit,
}: {
  pending: boolean;
  onClose: () => void;
  onSubmit: (v: {
    model: string; manufacturer: string; reportedModel?: string;
    powerKw?: number | null; connectors?: number | null; notes?: string;
  }) => void;
}) {
  const [model, setModel] = useState('');
  const [make, setMake] = useState('');
  const [reported, setReported] = useState('');
  const [kw, setKw] = useState('');
  const [guns, setGuns] = useState('');
  const [notes, setNotes] = useState('');

  const ok = model.trim().length >= 2 && make.trim().length >= 2;

  return (
    <div className="drawer">
      <div className="drawer-body">
        <h3>Add a charger model</h3>

        <label className="lbl">Model</label>
        <input
          className="inp" autoFocus value={model}
          placeholder="YCL-dual-44KW"
          onChange={(e) => setModel(e.target.value)}
        />
        <div className="li-sub" style={{ marginTop: 4, marginBottom: 10 }}>
          The full name, as you want it on a charge point and on paperwork. It
          cannot be changed afterwards: it is what every unit set to this model
          stores, and what decides how the unit is handled.
        </div>

        <label className="lbl">Manufacturer</label>
        <input
          className="inp" value={make}
          placeholder="YCL"
          onChange={(e) => setMake(e.target.value)}
        />

        <label className="lbl" style={{ marginTop: 10 }}>Reports itself as</label>
        <input
          className="inp mono" value={reported}
          placeholder="YC-44KW — leave empty if it reports the full name"
          onChange={(e) => setReported(e.target.value)}
        />
        <div className="li-sub" style={{ marginTop: 4, marginBottom: 10 }}>
          OCPP allows only 20 characters for a model, so most units abbreviate.
          Recording it here is how you can tell which entry an uncorrected
          charge point is.
        </div>

        <div className="row" style={{ gap: 8 }}>
          <div style={{ flex: 1 }}>
            <label className="lbl">Power (kW)</label>
            <input
              className="inp" value={kw} inputMode="decimal"
              placeholder="44"
              onChange={(e) => setKw(e.target.value.replace(/[^\d.]/g, ''))}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label className="lbl">Guns</label>
            <input
              className="inp" value={guns} inputMode="numeric"
              placeholder="2"
              onChange={(e) => setGuns(e.target.value.replace(/\D/g, ''))}
            />
          </div>
        </div>

        <label className="lbl" style={{ marginTop: 10 }}>Notes</label>
        <input
          className="inp" value={notes}
          placeholder="Anything odd about this hardware"
          onChange={(e) => setNotes(e.target.value)}
        />

        <div className="row" style={{ gap: 8, marginTop: 12 }}>
          <button
            className="btn"
            disabled={pending || !ok}
            onClick={() => onSubmit({
              model: model.trim(),
              manufacturer: make.trim(),
              reportedModel: reported.trim() || undefined,
              powerKw: kw.trim() === '' ? null : Number(kw),
              connectors: guns.trim() === '' ? null : Number(guns),
              notes: notes.trim() || undefined,
            })}
          >
            {pending ? 'Adding…' : 'Add'}
          </button>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
